import { isGoogleCloudStorageUrl } from "mt-commands/helpers/isGoogleCloudStorageUrl";
import Head from "next/head";
import Image, { getImageProps } from "next/image";
import type { ImgHTMLAttributes } from "react";
import { useContext } from "react";
import { CloudinaryContext } from "./Provider";
import { CloudinaryTransformationParamKey } from "./constants";

const cloudinaryImageDefaultTransformations = ["q_auto", "f_webp", "dpr_2.0"];

type Transformations = Array<CloudinaryTransformationParamKey>;

// A value per screen. The browser picks the URL from the HTML, so the image is
// downloaded once at the right size without waiting for JavaScript.
export type Responsive<T> = { mobile: T; tablet: T; web: T };
type Screen = keyof Responsive<unknown>;

export function isResponsive<T>(
  value: T | Responsive<T> | undefined,
): value is Responsive<T> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "web" in value
  );
}

export function forScreen<T>(value: T | Responsive<T>, screen: Screen): T {
  return isResponsive(value) ? value[screen] : value;
}

// $breakpoint-fm and $breakpoint-ml in styles/_layout.scss.
export const MOBILE_MEDIA = "(max-width: 559px)";
export const TABLET_MEDIA = "(min-width: 560px) and (max-width: 839px)";
const WEB_MEDIA = "(min-width: 840px)";

const BLUR_DATA_URL =
  "data:image/jpeg;base64,/9j/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q==";

interface CloudinaryImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src"
> {
  // A string applies to every screen.
  src?: string | Responsive<string>;
  // An array applies to every screen.
  transformations?: Transformations | Responsive<Transformations>;
  objPosition?: string;
  objFit?: any;
  isPriority?: boolean;
  layout?: React.ComponentProps<typeof Image>["layout"];
  width?: React.ComponentProps<typeof Image>["width"];
  height?: React.ComponentProps<typeof Image>["height"];
}

export function CloudinaryImageUrl({
  src,
  transformations,
}: {
  src?: string;
  transformations?: Transformations;
}) {
  const { cloudName, autoUploadMappingFolder } = useContext(CloudinaryContext);
  const stringifiedParams = cloudinaryImageDefaultTransformations
    .concat(transformations || [])
    .join(",");

  if (src && isGoogleCloudStorageUrl(src)) {
    return `https://res.cloudinary.com/${cloudName}/image/fetch/${stringifiedParams}/${src}`;
  }

  const cloudinaryStagingRoot = `https://res.cloudinary.com/${cloudName}/image/upload/${stringifiedParams}/v1/${autoUploadMappingFolder[0]?.folder}/`;
  const cloudinaryProductionRoot = `https://res.cloudinary.com/${cloudName}/image/upload/${stringifiedParams}/v1/${autoUploadMappingFolder[0]?.folder}/`;

  const url = src
    ?.replace(`${autoUploadMappingFolder[0]?.urlPrefix}`, cloudinaryStagingRoot)
    ?.replace(
      `${autoUploadMappingFolder[0]?.urlPrefix}`,
      cloudinaryProductionRoot,
    )
    ?.replace(/\.(jpg|jpeg|png)(\?.*)?$/i, ".webp$2");

  if (!url || !url.includes("cloudinary")) {
    const appendRoot =
      `https://res.cloudinary.com/${cloudName}/image/upload/${stringifiedParams}/v1/` +
      src;
    return appendRoot;
  }

  if (url && !url.includes(stringifiedParams) && !url.includes("/v1/tours/")) {
    return url.replace(
      `https://res.cloudinary.com/${cloudName}/image/upload/`,
      `https://res.cloudinary.com/${cloudName}/image/upload/${stringifiedParams}/`,
    );
  }

  return url;
}

export function CloudinaryImage({
  src,
  alt,
  transformations,
  objPosition,
  isPriority,
  layout,
  width,
  height,
  objFit,
  fetchPriority,
  loading,
}: CloudinaryImageProps) {
  const urlFor = (screen: Screen) =>
    CloudinaryImageUrl({
      src: forScreen(src, screen),
      transformations: forScreen(transformations, screen),
    });
  const imageSrc = urlFor("web");

  const imageProps = {
    unoptimized: true,
    src: imageSrc!,
    fill: layout ? false : true,
    fetchPriority: fetchPriority ?? (isPriority ? "high" : "low"),
    alt: alt || "image",
    width,
    height,
    loading: loading ?? (isPriority ? "eager" : "lazy"),
    style: {
      maxWidth: "100%",
      objectFit: objFit ? objFit : "cover",
      objectPosition: objPosition ? objPosition : "center",
    },
  } as const;

  if (isResponsive(src) || isResponsive(transformations)) {
    const sources = [
      { media: MOBILE_MEDIA, src: urlFor("mobile") },
      { media: TABLET_MEDIA, src: urlFor("tablet") },
    ];
    // getImageProps can't remove a blur placeholder after load, so it's omitted here.
    const { props } = getImageProps(imageProps);
    return (
      <>
        {isPriority && (
          // next/image's `preload` doesn't cover <picture>, so preload each source
          // for the screens that will use it.
          <Head>
            {[...sources, { media: WEB_MEDIA, src: imageSrc }].map(
              ({ media, src }) => (
                <link
                  key={`preload-${media}-${src}`}
                  rel="preload"
                  as="image"
                  href={src}
                  media={media}
                  fetchPriority="high"
                />
              ),
            )}
          </Head>
        )}
        <picture>
          {sources.map(({ media, src }) => (
            <source key={media} media={media} srcSet={src} />
          ))}
          <img {...props} />
        </picture>
      </>
    );
  }

  return (
    <Image
      {...imageProps}
      placeholder="blur"
      blurDataURL={BLUR_DATA_URL}
      preload={isPriority}
    />
  );
}

export default CloudinaryImage;
